import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { IQueryStorageItem } from 'app/query-builder/core/query-storage.service';
import { requiredWhen } from 'core/validators';
import { SimpleQueryReducer } from 'query-builder/core/simple-query-reducer';

import {
  EntitiesReportInput, EntityConnectionTypeDetailsFragment, ExportEntitiesGQL,
  GetEntityConnectionTypesGQL
} from 'generated/graphql';

@Component({
  selector: 'app-export-specialized',
  templateUrl: './export-specialized.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExportSpecializedComponent implements OnInit {
  @Input() query: IQueryStorageItem;
  @Input() recordCount: number;

  formGroup = this._makeFormGroup();

  /**
   * Helper to get formGroup value
   */
  get specializedExportType() {
    return this.formGroup.value?.specializedExportType as string;
  }

  /**
   * Helper to get formGroup value
   */
  get selectedEntityConnectionType() {
    return this.formGroup.value?.selectedEntityConnectionType;
  }

  connectionTypeOptions: EntityConnectionTypeDetailsFragment[] = [];

  @Output() exportSuccess = new EventEmitter<boolean>();

  constructor(
    private _exportEntitiesGQL: ExportEntitiesGQL,
    private _getEntityConnectionTypesGQL: GetEntityConnectionTypesGQL,
    private _cd: ChangeDetectorRef
  ) {
  }

  ngOnInit() {
    this._getEntityConnectionTypesGQL
      .fetch({ campaignId: this.query.campaignId })
      .subscribe(
        ({ data }) => {
          this.connectionTypeOptions = data.getEntityConnectionTypes;
          this._cd.markForCheck();
        });
  }

  onSpecializedExportSubmit() {
    const input = this._makeMutationInput();

    this._exportEntitiesGQL
      .mutate({ input })
      .subscribe(
        (response) => {
          const wasSuccess = response.data?.entitiesReport?.success;
          this.exportSuccess.emit(wasSuccess);
        },
        err => {
          console.error(err);
          this.exportSuccess.emit(false);
        }
      );
  }

  private _makeMutationInput(): EntitiesReportInput {
    const reducedQuery = new SimpleQueryReducer(this.query.query).reduce();
    const isConnection = this.specializedExportType === 'entity_connections';
    const extraParams = isConnection ? {
      entity_connection_type_id: this.formGroup.value.selectedEntityConnectionType.id
    } : undefined;

    return {
      params: {
        reportType: this.specializedExportType,
        campaignId: this.query.campaignId,
        fields: null,
        extraParams,
        queryJson: JSON.stringify(reducedQuery)
      }
    };
  }

  private _makeFormGroup() {
    const specializedExportType = new UntypedFormControl(null, {
      validators: Validators.required
    });
    const selectedEntityConnectionType = new UntypedFormControl(null, {
      validators: requiredWhen(() => {
        setTimeout(() => this._cd.markForCheck(), 10);
        return specializedExportType.value === 'entity_connections';
      })
    });
    return new UntypedFormGroup({
      specializedExportType,
      selectedEntityConnectionType
    });
  }
}
